import flag from './extensions/flag.json';
import investigateNewWorkbench from './extensions/investigateNewWorkbench.json';
import investigateSourceWorkbench from './extensions/investigateSourceWorkbench.json';
import labels from './extensions/labels.json';
import notes from './extensions/notes.json';
import reviewed from './extensions/reviewed.json';
import editManualEvent from './extensions/editManualEvent.json';
import suppression from './extensions/suppression.json';
import { VantageNames } from '@/vantage/constants';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import type { ExtensionSchema } from '@/vantage/types/Extension.types';
import { upgradeExtensions } from '@/vantage/extensionsUpgrader';
import { isVantageNameBasedAssetContextEnabled } from '@/services/systemConfiguration.utilities';
import { safeGetJsonProperty } from '@/utilities/httpHelpers.utilities';
import { getExtensions } from '@/vantage/utilities';

export const bundledExtensions = [
  flag,
  investigateNewWorkbench,
  investigateSourceWorkbench,
  labels,
  notes,
  reviewed,
  suppression,
  editManualEvent,
] as ExtensionSchema[];

export const ALWAYS_INCLUDED_EXTENSIONS = [
  VantageNames.Extensions.Flag,
  VantageNames.Extensions.Reviewed,
  VantageNames.Extensions.Labels,
  VantageNames.Extensions.Suppression,
  VantageNames.Extensions.EditManualEvent,
];

/** Daily */
export const DEFAULT_CRON_SCHEDULE = ['0 0 0 ? * 1,2,3,4,5,6,7'];

export function getExtensionConfigs(extensions: ExtensionSchema[]) {
  let install: ExtensionSchema[] = [];
  let enabled = extensions.filter(({ enabled }) => enabled);

  if (!extensions.length) {
    install = [...bundledExtensions];
    enabled = [...bundledExtensions].map((ext) => ({ ...ext, enabled: true } as ExtensionSchema));
    return { install, enabled };
  }

  if (!enabled.some((ext) => ext.name === VantageNames.Extensions.Notes)) {
    install.push(notes as ExtensionSchema);
    enabled.push(notes as ExtensionSchema);
  }

  for (const extensionName of ALWAYS_INCLUDED_EXTENSIONS) {
    if (!extensions.some((ext) => ext.name === extensionName)) {
      const extension = bundledExtensions.find((ext) => ext.name === extensionName);
      install.push({ ...extension, enabled: false } as ExtensionSchema);
    }
  }

  return { install, enabled: enabled.map(upgradeExtensions) };
}

export async function installExtensions(workbookId: string, extensions: ExtensionSchema[]) {
  const installedExtensions = await getExtensions(workbookId);
  await sqItemsApi.setProperties(
    [{ name: 'extensions', value: JSON.stringify(installedExtensions.concat(extensions)) }],
    {
      id: workbookId,
    },
  );
}

export const tableConfig = () => ({
  purviewFields: [VantageNames.Columns.Condition, VantageNames.Columns.Asset],
  uniquenessMetaContextMappings: {
    Condition: VantageNames.Columns.ConditionID,
    Asset: VantageNames.Columns.AssetUUID,
  },
  defaultDateRange: {
    lookback: '2 days',
    lookahead: '14 days',
    clockOverride: '3:00:00',
  },
  contextItemIdMapping: {
    Asset: VantageNames.Columns.AssetUUID,
  },
});

export async function initializeVantage(workbookId: string) {
  const isManualEventsEnabled = await safeGetJsonProperty(workbookId, 'isManualEventsEnabled', false);
  const properties = {
    extensions: bundledExtensions,
    tableConfig: tableConfig(),
    includeExtinct: false,
  };
  await sqItemsApi.setProperties(
    [
      { name: 'extensions', value: JSON.stringify(properties.extensions) },
      { name: 'includeExtinct', value: JSON.stringify(properties.includeExtinct) },
      { name: 'tableConfig', value: JSON.stringify(properties.tableConfig) },
    ],
    {
      id: workbookId,
    },
  );
  return {
    workbookId,
    ...properties,
    nameBasedAssetContextOverride: isVantageNameBasedAssetContextEnabled() as unknown as boolean,
    isManualEventsEnabled,
  };
}
